/* Verbatim port of the §4 relationship graph (SVG force layout) from
 * service/review/index.html: VIEW (~2751), svgEl/initGraph (~2752-2767),
 * fitView (~2768-2776), runSim (~2777-2800), shortTitle (~2801),
 * drawGraph (~2802-2855), applyView/zoomBy (~2856-2863),
 * bindGraphEvents (~2864-2911), styleGraphSelection (~2914-2941),
 * redrawPositions (~2943-2954).
 *
 * PARAMETERIZED (rule a): every function that read the global `STATE`
 * (directly, or transitively via coreAsset()/influenceScore()) now takes
 * `state: AppPayloadState` as an explicit parameter — appended after any
 * pre-existing parameters (runSim(W,H) -> runSim(W,H,state), same
 * placement convention as lib/icons.ts's apiBase), or as the sole
 * parameter where the original took none (drawGraph, bindGraphEvents,
 * redrawPositions). `STATE.nodes = new Map(...)` (initGraph, the one
 * WRITE to state in this module) becomes `state.nodes = ...` — a direct
 * mutation of the same long-lived state object the caller holds, same
 * treatment as ui/drawer.ts's `state.selected = id`.
 *
 * `styleGraphSelection(state)` already matches the signature ui/drawer.ts
 * (openDrawer/closeDrawer) was written against — this file's F2a stub is
 * replaced wholesale now that the real graph exists.
 *
 * DEVIATION (module state): `VIEW` (originally ~2751, `let VIEW = {x:0,
 * y:0, k:1}`) stays a module-local mutable object, same TL_STATE-style
 * treatment as views/timeline.ts — only this module's own functions
 * (fitView/zoomBy/applyView) ever read or reassign it.
 *
 * DEVIATION (circular import): bindGraphEvents' pointerup handler calls
 * openDrawer()/closeDrawer() (ui/drawer.ts); ui/drawer.ts's
 * openDrawer/closeDrawer call styleGraphSelection() (this file) to
 * spotlight the selection. Same real circular import already established
 * for search.ts <-> timeline.ts <-> popover.ts — safe because every
 * cross-reference here is read inside a function/event-handler body, never
 * at module-eval time.
 *
 * DEVIATION (typing): the original is fully duck-typed (graph nodes are
 * plain `{a, x, y, vx, vy, r}` records built ad hoc in initGraph, DOM
 * event targets walked with `.closest`/`.dataset`). `state.nodes` is typed
 * `Map<string, unknown>` in AppPayloadState (state-shape.ts) for shape
 * completeness; Batch C gave it the real `Map<string, GraphNode>` type
 * (derive/state-shape.ts), so the old per-site `Map<string, any>` casts and
 * `any`-typed pointer handlers are gone.
 *
 * DEVIATION (typing): `styleGraphSelection`'s three inline-style writes
 * (`g.style.opacity = 1 : .14`, `l.style.opacity = .9 : 1 : .06`,
 * `l.style.strokeWidth = 2.4 : ''`) assigned bare numbers in the original —
 * `CSSStyleDeclaration` properties are `string`-typed in TS's DOM lib, so
 * each numeric literal is quoted here ('1', '.14', '2.4', ...). No behavior
 * change: the browser's CSSOM setter ToStrings a numeric assignment the
 * same way (`el.style.opacity = 1` and `el.style.opacity = '1'` produce an
 * identical computed style) — this only satisfies the compiler.
 */
import { coreAsset, influenceScore } from '../derive/exec.ts';
import { BAD_STATUS, CAT_COLOR, CAT_LABEL } from '../derive/axes.ts';
import { esc, faviconUrl, fixMojibake } from '../lib/format.ts';
import { closeDrawer, openDrawer } from './drawer.ts';
import type { AppPayloadState, GraphNode } from '../derive/state-shape.ts';
import type { NormalizedAsset } from '../types/contract.ts';

let VIEW = { x: 0, y: 0, k: 1 };

function svgEl(): HTMLElement | null { return document.getElementById('map'); }

export function initGraph(state: AppPayloadState): void {
  const svg = svgEl(); if (!svg) return;
  const W = svg.clientWidth || 1100, H = 640;
  const core = coreAsset(state);
  const nodes = new Map<string, GraphNode>(state.assets.map((a, i) => {
    const angle = (i / state.assets.length) * Math.PI * 2;
    const r = a.isCoreEntity ? 0 : 230 + (i % 5) * 30;
    // node size = measured reputation influence (search position, AI
    // citations, page-1 rank); the core entity is always the anchor size
    const nr = a.isCoreEntity ? 30 : Math.round(8 + influenceScore(state, a) * 16);
    return [a.id, { a, x: W/2 + Math.cos(angle)*r, y: H/2 + Math.sin(angle)*r, vx:0, vy:0, r: nr }];
  }));
  state.nodes = nodes;
  if (core) { const n = nodes.get(core.id); if (n) { n.x = W/2; n.y = H/2; } }
  runSim(W, H, state); drawGraph(state); fitView(state);
}
function fitView(state: AppPayloadState): void {
  const svg = svgEl(); const W = svg!.clientWidth || 1100, H = 640, pad = 70;
  const ns = [...state.nodes.values()]; if (!ns.length) return;
  const minX = Math.min(...ns.map(n => n.x)) - pad, maxX = Math.max(...ns.map(n => n.x)) + pad;
  const minY = Math.min(...ns.map(n => n.y)) - pad, maxY = Math.max(...ns.map(n => n.y)) + pad;
  const k = Math.min(1.15, W / (maxX - minX), H / (maxY - minY));
  VIEW = { k, x: (W - (minX + maxX) * k) / 2, y: (H - (minY + maxY) * k) / 2 };
  applyView();
}
function runSim(W: number, H: number, state: AppPayloadState): void {
  const nodesMap = state.nodes;
  const nodes = [...nodesMap.values()];
  const edges = state.edges.map((e) => [nodesMap.get(e.from), nodesMap.get(e.to)])
    .filter((p): p is [GraphNode, GraphNode] => !!(p[0] && p[1]));
  for (let tick = 0; tick < 320; tick++){
    const alpha = 1 - tick/320;
    for (let i = 0; i < nodes.length; i++){
      const n = nodes[i];
      if (!n) continue; // loop bound keeps i in range — narrows for TS
      n.vx += (W/2 - n.x) * 0.002 * alpha; n.vy += (H/2 - n.y) * 0.0024 * alpha;
      for (let j = i+1; j < nodes.length; j++){
        const m = nodes[j];
        if (!m) continue; // loop bound keeps j in range — narrows for TS
        let dx = n.x - m.x, dy = n.y - m.y, d2 = dx*dx + dy*dy || 1;
        if (d2 < 52900){ const f = 3200 * alpha / d2; dx *= f; dy *= f;
          n.vx += dx; n.vy += dy; m.vx -= dx; m.vy -= dy; }
      }
    }
    edges.forEach(([s,t]) => {
      const ideal = (s.a.isCoreEntity || t.a.isCoreEntity) ? 185 : 110;
      let dx = t.x - s.x, dy = t.y - s.y, d = Math.sqrt(dx*dx + dy*dy) || 1;
      const f = (d - ideal) / d * 0.02 * (1 - tick/400);
      dx *= f; dy *= f; s.vx += dx; s.vy += dy; t.vx -= dx; t.vy -= dy;
    });
    nodes.forEach(n => { n.x += n.vx; n.y += n.vy; n.vx *= .6; n.vy *= .6; });
  }
}
function shortTitle(a: NormalizedAsset): string { const t = a.title || a.id; return t.length > 26 ? t.slice(0, 24) + '…' : t; }
function drawGraph(state: AppPayloadState): void {
  const svg = svgEl()!;
  const nodesMap = state.nodes;
  const edgeEls = state.edges.map((e, i) => {
    const s = nodesMap.get(e.from), t = nodesMap.get(e.to);
    if (!s || !t) return '';
    const observed = e.observed === true;
    return `<line data-ei="${i}" x1="${s.x}" y1="${s.y}" x2="${t.x}" y2="${t.y}"
      stroke="${observed ? 'var(--azure)' : 'var(--line)'}" stroke-width="${observed ? 1.8 : 1.2}"
      ${!observed && e.evidence ? 'stroke-dasharray="2 5"' : ''} opacity=".9"><title>${esc(e.relation)}${observed ? ' · observed ('+esc(e.evidence)+')' : ' · asserted'}</title></line>`;
  }).join('');
  // ghost edges: the top missing bridges render as recommendations — the link
  // that SHOULD exist between the core and an unanchored client property
  const core = coreAsset(state);
  const coreNode = core && nodesMap.get(core.id);
  const observedPairs = new Set(state.edges.filter((e) => e.observed)
    .flatMap((e) => [e.from + '|' + e.to, e.to + '|' + e.from]));
  const ghosts = (state.linkGraph?.bridges || [])
    .filter((b) => core && coreNode && nodesMap.get(b.assetId)
      && !observedPairs.has(core.id + '|' + b.assetId))
    .slice(0, 8).map((b) => {
      const n = nodesMap.get(b.assetId);
      if (!n || !coreNode) return ''; // filter above guarantees both — narrows for TS
      return `<line data-ghost="${esc(b.assetId)}" x1="${coreNode.x}" y1="${coreNode.y}" x2="${n.x}" y2="${n.y}"
        stroke="var(--amber)" stroke-width="2" stroke-dasharray="6 5" opacity=".8">
        <title>missing bridge — ${esc(fixMojibake(b.title))}: ${b.fixes.map(esc).join('; ')}</title></line>`;
    }).join('');
  const edgeAll = edgeEls + ghosts;
  const nodeEls = [...nodesMap.values()].map((n) => {
    const a = n.a, bad = BAD_STATUS.includes(a.status);
    const fav = faviconUrl(a.url, 64), ir = n.r * 0.62;
    return `<g class="node" data-id="${a.id}" transform="translate(${n.x},${n.y})" style="cursor:pointer">
      ${bad ? `<circle r="${n.r + 5}" fill="none" stroke="var(--neg)" stroke-width="2" stroke-dasharray="4 3"/>` : ''}
      ${a.isCoreEntity ? `<circle r="${n.r + 8}" fill="none" stroke="var(--slate)" stroke-width="1.5" opacity=".35"/>` : ''}
      <circle class="selring" r="${n.r + 5}" fill="none" stroke="var(--azure)" stroke-width="2.5" style="display:none"/>
      <circle class="dot" r="${n.r}" fill="var(--white)" stroke="${CAT_COLOR[a.category] || 'var(--sent-unrated)'}" stroke-width="${a.isCoreEntity ? 4.5 : 3.5}"/>
      ${fav ? `<image href="${fav}" x="${-ir}" y="${-ir}" width="${ir*2}" height="${ir*2}"/>`
            : `<circle r="${n.r * 0.45}" fill="${CAT_COLOR[a.category] || 'var(--sent-unrated)'}"/>`}
      <text class="lbl" y="${n.r + 13}" text-anchor="middle" font-family="var(--sans)" font-size="${a.isCoreEntity ? 12.5 : 9.5}"
        font-weight="${a.isCoreEntity ? 700 : 500}" fill="var(--ink)">${esc(shortTitle(a))}</text>
    </g>`;
  }).join('');
  svg.innerHTML = `<g id="viewport">${edgeAll}${nodeEls}</g>`;
  applyView(); bindGraphEvents(state);
  document.getElementById('mapLegend')!.innerHTML =
    Object.entries(CAT_COLOR).map(([k,c]) => `<span><i style="background:${c}"></i>${CAT_LABEL[k]}</span>`).join('') +
    `<span><i style="background:var(--azure);height:3px;border-radius:2px"></i>observed link (crawl / markup / Wikidata)</span>` +
    `<span><i style="background:transparent;border-top:2px dotted var(--muted);height:0"></i>asserted by research only</span>` +
    (ghosts ? `<span><i style="background:transparent;border-top:2px dashed var(--amber);height:0"></i>missing bridge — build it</span>` : '');
  const observed = state.edges.filter((e) => e.observed === true).length;
  const orphN = (state.linkGraph?.orphans || []).length;
  document.getElementById('mapSummary')!.innerHTML =
    `${state.assets.length} surfaces · ${state.edges.length} links, ${observed} observed by the crawler` +
    (orphN ? ` · <span style="color:var(--neg)">${orphN} orphaned client properties</span>` : '') +
    ` · node size = measured reputation influence (search position × AI citations) · amber dashes = missing bridges from §2 · click a node to focus it`;
}
function applyView(): void { const vp = document.getElementById('viewport'); if (vp) vp.setAttribute('transform', `translate(${VIEW.x},${VIEW.y}) scale(${VIEW.k})`); }
function zoomBy(factor: number, cx?: number, cy?: number): void {
  const svg = svgEl()!; const rect = svg.getBoundingClientRect();
  const mx = cx ?? rect.width/2, my = cy ?? rect.height/2;
  const k2 = Math.min(3, Math.max(.3, VIEW.k * factor));
  VIEW.x = mx - (mx - VIEW.x) * k2 / VIEW.k; VIEW.y = my - (my - VIEW.y) * k2 / VIEW.k; VIEW.k = k2;
  applyView();
}
function bindGraphEvents(state: AppPayloadState): void {
  const svg = svgEl()!;
  const nodesMap = state.nodes;
  document.getElementById('zin')!.onclick = () => zoomBy(1.25);
  document.getElementById('zout')!.onclick = () => zoomBy(.8);
  document.getElementById('zfit')!.onclick = () => fitView(state);
  const tt = document.getElementById('tooltip')!;
  let panning = false, panMoved = false, dragNode: GraphNode | null = null, px = 0, py = 0;
  svg.onpointerdown = (ev: PointerEvent) => {
    if (ev.pointerType !== 'mouse') return;
    const g = (ev.target as Element).closest<HTMLElement>('.node');
    px = ev.clientX; py = ev.clientY;
    if (g){ dragNode = nodesMap.get(g.dataset.id ?? '') ?? null; if (dragNode) dragNode._moved = false; }
    else { panning = true; panMoved = false; svg.classList.add('dragging'); }
    svg.setPointerCapture(ev.pointerId);
  };
  svg.onpointermove = (ev: PointerEvent) => {
    const dx = ev.clientX - px, dy = ev.clientY - py;
    if (dragNode){
      if (Math.abs(dx) + Math.abs(dy) > 3) dragNode._moved = true;
      dragNode.x += dx / VIEW.k; dragNode.y += dy / VIEW.k; px = ev.clientX; py = ev.clientY;
      redrawPositions(state);
    } else if (panning){
      if (Math.abs(dx) + Math.abs(dy) > 3) panMoved = true;
      VIEW.x += dx; VIEW.y += dy; px = ev.clientX; py = ev.clientY; applyView();
    } else {
      const g = (ev.target as Element).closest<HTMLElement>('.node');
      const node = g && nodesMap.get(g.dataset.id ?? '');
      if (node){ const a = node.a;
        tt.style.display = 'block'; tt.style.left = (ev.clientX + 14) + 'px'; tt.style.top = (ev.clientY + 10) + 'px';
        tt.innerHTML = `<strong>${esc(a.title)}</strong><div class="tt-type">${[CAT_LABEL[a.category], a.assetType, a.status].filter(Boolean).map(esc).join(' · ')}</div>`;
      } else tt.style.display = 'none';
    }
  };
  svg.onpointerup = (_ev: PointerEvent) => {
    if (dragNode && !dragNode._moved){
      openDrawer(state, dragNode.a.id);          // detail pane + spotlight
    } else if (panning && !panMoved){
      closeDrawer(state);                         // empty-canvas click clears focus
    }
    dragNode = null; panning = false; svg.classList.remove('dragging');
    tt.style.display = 'none';
  };
  svg.onwheel = (ev: WheelEvent) => {
    if (!ev.ctrlKey && !ev.metaKey) return;
    ev.preventDefault();
    const rect = svg.getBoundingClientRect();
    zoomBy(ev.deltaY < 0 ? 1.12 : .89, ev.clientX - rect.left, ev.clientY - rect.top);
  };
}
/* focus mode: selecting a node spotlights it + its direct connections and
   dims the rest; clearing the selection restores the full graph */
export function styleGraphSelection(state: AppPayloadState): void {
  const svg = svgEl(); if (!svg || !svg.querySelector('.node')) return;
  const sel = state.selected as string | null;
  const nodesMap = state.nodes;
  let hood: Set<string | undefined> | null = null;
  if (sel && nodesMap.has(sel)){
    hood = new Set<string | undefined>([sel]);
    state.edges.forEach((e) => {
      if (e.from === sel) hood!.add(e.to);
      if (e.to === sel) hood!.add(e.from);
    });
  }
  svg.querySelectorAll<HTMLElement>('.node').forEach(g => {
    const id = g.dataset.id;
    g.style.opacity = (!hood || hood.has(id)) ? '1' : '.14';
    const ring = g.querySelector<HTMLElement>('.selring');
    if (ring) ring.style.display = sel === id ? '' : 'none';
  });
  svg.querySelectorAll<HTMLElement>('line[data-ei]').forEach(l => {
    const e = state.edges[+l.dataset.ei!]; if (!e) return;
    const touches = sel && (e.from === sel || e.to === sel);
    l.style.opacity = !sel ? '.9' : touches ? '1' : '.06';
    l.style.strokeWidth = touches ? '2.4' : '';
  });
  svg.querySelectorAll<HTMLElement>('line[data-ghost]').forEach(l => {
    const touches = sel && l.dataset.ghost === sel;
    l.style.opacity = !sel ? '.8' : touches ? '1' : '.06';
  });
}

function redrawPositions(state: AppPayloadState): void {
  const nodesMap = state.nodes;
  document.querySelectorAll<HTMLElement>('#map .node').forEach(g => {
    const n = nodesMap.get(g.dataset.id!);
    if (!n) return; // every .node was rendered from this map — narrows for TS
    g.setAttribute('transform', `translate(${n.x},${n.y})`);
  });
  document.querySelectorAll<SVGLineElement>('#map line').forEach(l => {
    const e = state.edges[+l.dataset.ei!]; if (!e) return;
    const s = nodesMap.get(e.from), t = nodesMap.get(e.to);
    if (!s || !t) return; // drawGraph skipped edges missing either end
    // String(...): setAttribute is string-typed; the untyped code passed the
    // number and relied on the same ToString coercion
    l.setAttribute('x1', String(s.x)); l.setAttribute('y1', String(s.y));
    l.setAttribute('x2', String(t.x)); l.setAttribute('y2', String(t.y));
  });
}
